import { useState, useCallback } from 'react'

export interface WorksheetOpenOptions {
  searchTerm?: string
  autoSearch?: boolean
  tab?: 'rhymes' | 'thesaurus'
  parentWordId?: string
}

export interface WorksheetState {
  isOpen: boolean
  searchTerm: string
  activeTab: 'rhymes' | 'thesaurus'
  parentWordId: string | null
  searchNonce: number
  lastOptions: WorksheetOpenOptions | null
  openWorksheet: (options?: WorksheetOpenOptions) => void
  closeWorksheet: () => void
  setSearchTerm: (term: string) => void
  setActiveTab: (tab: 'rhymes' | 'thesaurus') => void
}

/**
 * Hook for managing the exploration worksheet panel state
 * Handles opening/closing, search terms, tab switching, and parent word tracking
 */
export function useWorksheetState(): WorksheetState {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTermState] = useState('')
  const [activeTab, setActiveTabState] = useState<'rhymes' | 'thesaurus'>('thesaurus')
  const [parentWordId, setParentWordId] = useState<string | null>(null)
  const [searchNonce, setSearchNonce] = useState(0)
  const [lastOptions, setLastOptions] = useState<WorksheetOpenOptions | null>(null)

  const openWorksheet = useCallback((options?: WorksheetOpenOptions) => {
    setIsOpen(true)

    if (options) {
      setLastOptions(options)

      if (options.searchTerm !== undefined) {
        setSearchTermState(options.searchTerm)
      }

      if (options.tab) {
        setActiveTabState(options.tab)
      }

      if (options.parentWordId !== undefined) {
        setParentWordId(options.parentWordId)
      }

      // Increment nonce to force re-search even if same word is requested
      setSearchNonce(prev => prev + 1)
    }
  }, [])

  const closeWorksheet = useCallback(() => {
    setIsOpen(false)
  }, [])

  const setSearchTerm = useCallback((term: string) => {
    setSearchTermState(term)
  }, [])

  const setActiveTab = useCallback((tab: 'rhymes' | 'thesaurus') => {
    setActiveTabState(tab)
  }, [])

  return {
    isOpen,
    searchTerm,
    activeTab,
    parentWordId,
    searchNonce,
    lastOptions,
    openWorksheet,
    closeWorksheet,
    setSearchTerm,
    setActiveTab
  }
}
